import { $applyNodeReplacement, $createParagraphNode, ElementNode } from 'lexical';

/**
 * A callout block (documents redesign card 2.5): a boxed paragraph with an
 * icon, in three kinds — info (accent), warning (amber), success (green). Each
 * kind also has its own icon, so it never differs by colour alone.
 *
 * Shaped like Lexical's QuoteNode — it holds inline content directly (Shift
 * Enter for a line break, Enter leaves it for a new paragraph, Backspace at
 * the start turns it back into a paragraph) — so every place that already
 * understands a quote's shape (HTML, Markdown, PDF) needs one small case.
 *
 * Saved-HTML shape (exportDOM writes it, importDOM claims it, sanitizeHtml.js
 * keeps exactly this attribute with exactly these values):
 *   <div data-callout="info|warning|success">…inline…</div>
 * Markdown (git sync Stage 2 + export): GitHub's `> [!NOTE]` / `> [!WARNING]` /
 * `> [!TIP]` (utils/docMarkdown/callout.js).
 *
 * Registered for EVERY editor host, like the media nodes: a client that cannot
 * parse a node type drops the block and its next autosave writes the document
 * back without it.
 */

export const CALLOUT_KINDS = Object.freeze(['info', 'warning', 'success']);
export const isCalloutKind = (kind) => CALLOUT_KINDS.includes(kind);
const kindOf = (kind) => (isCalloutKind(kind) ? kind : 'info');

function $convertCalloutElement(domNode) {
  return { node: $createCalloutNode(domNode.getAttribute('data-callout')) };
}

export class CalloutNode extends ElementNode {
  static getType() {
    return 'callout';
  }

  static clone(node) {
    return new CalloutNode(node.__kind, node.__key);
  }

  constructor(kind = 'info', key) {
    super(key);
    this.__kind = kindOf(kind);
  }

  getKind() {
    return this.getLatest().__kind;
  }

  setKind(kind) {
    const writable = this.getWritable();
    writable.__kind = kindOf(kind);
    return writable;
  }

  createDOM(config) {
    const div = document.createElement('div');
    const base = config.theme.callout || 'lx-callout';
    div.className = `${base} lx-callout--${this.__kind}`;
    div.setAttribute('data-callout', this.__kind);
    return div;
  }

  updateDOM(prevNode, dom, config) {
    if (prevNode.__kind !== this.__kind) {
      const base = config.theme.callout || 'lx-callout';
      dom.className = `${base} lx-callout--${this.__kind}`;
      dom.setAttribute('data-callout', this.__kind);
    }
    return false;
  }

  static importDOM() {
    return {
      div: (domNode) => {
        if (!domNode.getAttribute || !isCalloutKind(domNode.getAttribute('data-callout'))) return null;
        return { conversion: $convertCalloutElement, priority: 2 };
      },
    };
  }

  exportDOM() {
    const element = document.createElement('div');
    element.setAttribute('data-callout', this.__kind);
    if (this.isEmpty()) element.append(document.createElement('br'));
    return { element };
  }

  static importJSON(serialized) {
    return $createCalloutNode(serialized.kind).updateFromJSON(serialized);
  }

  exportJSON() {
    return { ...super.exportJSON(), kind: this.getKind() };
  }

  insertNewAfter(_, restoreSelection) {
    const paragraph = $createParagraphNode();
    paragraph.setDirection(this.getDirection());
    this.insertAfter(paragraph, restoreSelection);
    return paragraph;
  }

  collapseAtStart() {
    const paragraph = $createParagraphNode();
    this.getChildren().forEach((child) => paragraph.append(child));
    this.replace(paragraph);
    return true;
  }

  canMergeWhenEmpty() {
    return true;
  }
}

export function $createCalloutNode(kind = 'info') {
  return $applyNodeReplacement(new CalloutNode(kind));
}

export function $isCalloutNode(node) {
  return node instanceof CalloutNode;
}
